import { useState } from "react";
import { useSettings } from "@/lib/settings";
import PartnerModal from "@/components/PartnerModal";
import {
  Store, Wallet, Rocket, ShieldCheck, TrendingUp, Award, Users, HeartHandshake,
  GraduationCap, ArrowRight, Download, Quote, CheckCircle2,
} from "lucide-react";

const ICONS = { Store, Wallet, Rocket, ShieldCheck, TrendingUp, Award, Users, HeartHandshake, GraduationCap };

function ytEmbed(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

export default function PartnerWithUsPage() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  if (!settings) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;

  const p = settings.partner_page || {};
  if (p.enabled === false) {
    return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500" data-testid="partner-disabled">This page is currently unavailable.</div>;
  }
  const logo = settings.branding?.logo_light;
  const embed = ytEmbed(p.video_url);

  return (
    <div data-testid="partner-page">
      {/* Hero banner */}
      <section className="relative overflow-hidden bg-neutral-950 text-white">
        {p.hero_image && (
          <img src={p.hero_image} alt="Partner with EQtech Academy" loading="eager" decoding="async"
               className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="relative container-eq py-20 lg:py-28">
          {logo && <img src={logo} alt="EQtech Academy" className="h-10 mb-8 bg-white/90 rounded-lg px-3 py-1.5 w-fit" data-testid="partner-hero-logo" />}
          <div className="label-eq text-sky-400 mb-3">{p.hero_kicker || "Partner With Us"}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tight max-w-4xl leading-[1.05]" data-testid="partner-hero-title">
            {p.hero_title}
          </h1>
          <p className="mt-6 text-neutral-300 leading-relaxed text-base sm:text-lg max-w-3xl" data-testid="partner-hero-subtitle">
            {p.hero_subtitle}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button onClick={() => setOpen(true)} className="btn-primary-eq justify-center text-sm" data-testid="partner-cta-primary">
              {p.cta_primary_text || "Become a Partner"} <ArrowRight className="w-4 h-4" />
            </button>
            {p.brochure_url && (
              <a href={p.brochure_url} target="_blank" rel="noreferrer" download
                 className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-white/70 text-white font-semibold text-sm hover:bg-white hover:text-neutral-950 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400"
                 data-testid="partner-cta-brochure">
                <Download className="w-4 h-4" /> {p.cta_secondary_text || "Download Brochure"}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Video */}
      {embed && (
        <section className="py-16 lg:py-24 bg-white" data-testid="partner-video-section">
          <div className="container-eq max-w-4xl text-center">
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight mb-8">{p.video_heading || "See how partnership works"}</h2>
            <div className="relative w-full rounded-3xl overflow-hidden shadow-xl" style={{ aspectRatio: "16 / 9" }}>
              <iframe src={embed} title={p.video_heading || "Partner video"} loading="lazy"
                      className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {(p.features || []).length > 0 && (
        <section className="py-16 lg:py-24 bg-neutral-50" data-testid="partner-features-section">
          <div className="container-eq">
            <div className="text-center mb-12">
              <div className="label-eq text-sky-500 mb-3">Features</div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{p.features_heading}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {p.features.map((f, i) => {
                const Icon = ICONS[f.icon] || Store;
                return (
                  <div key={i} className="card-eq p-6" data-testid={`partner-feature-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-neutral-950 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-sky-400" />
                    </div>
                    <div className="font-display font-bold">{f.title}</div>
                    <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {(p.benefits || []).length > 0 && (
        <section className="py-16 lg:py-24 bg-white" data-testid="partner-benefits-section">
          <div className="container-eq">
            <div className="text-center mb-12">
              <div className="label-eq text-sky-500 mb-3">Partnership Benefits</div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{p.benefits_heading}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {p.benefits.map((b, i) => (
                <div key={i} className="flex gap-3 p-5 rounded-2xl border border-neutral-200 bg-neutral-50" data-testid={`partner-benefit-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-sm">{b.title}</div>
                    <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {(p.testimonials || []).length > 0 && (
        <section className="py-16 lg:py-24 bg-neutral-50" data-testid="partner-testimonials-section">
          <div className="container-eq">
            <div className="text-center mb-12">
              <div className="label-eq text-sky-500 mb-3">Testimonials</div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{p.testimonials_heading}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {p.testimonials.map((t, i) => (
                <div key={i} className="card-eq p-6" data-testid={`partner-testimonial-${i}`}>
                  <Quote className="w-6 h-6 text-sky-400" />
                  <p className="mt-4 text-sm text-neutral-700 leading-relaxed">"{t.quote}"</p>
                  <div className="mt-4 flex items-center gap-3 pt-4 border-t border-neutral-100">
                    {t.image && <img src={t.image} alt={t.name} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />}
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-neutral-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-neutral-950 text-white" data-testid="partner-cta-section">
        <div className="container-eq text-center max-w-2xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight leading-tight">{p.cta_heading || "Ready to grow with us?"}</h2>
          <p className="mt-5 text-neutral-400 leading-relaxed">{p.cta_subtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => setOpen(true)} className="btn-primary-eq justify-center text-sm" data-testid="partner-cta-bottom">
              {p.cta_primary_text || "Become a Partner"} <ArrowRight className="w-4 h-4" />
            </button>
            {p.brochure_url && (
              <a href={p.brochure_url} target="_blank" rel="noreferrer" download
                 className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-white/70 text-white font-semibold text-sm hover:bg-white hover:text-neutral-950 transition-colors">
                <Download className="w-4 h-4" /> {p.cta_secondary_text || "Download Brochure"}
              </a>
            )}
          </div>
        </div>
      </section>

      <PartnerModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
